/**
 * Generic TTL-based localStorage cache, generalizing the pattern already
 * used by HomeIdsContext (key + TTL + JSON stringify/parse + try/catch).
 * Purely a client-side optimization on top of the existing TanStack Query
 * cache — never a source of truth. Callers should keep treating the backend
 * as authoritative and simply skip a network round trip when a fresh entry
 * exists.
 */

const SCHEMA_VERSION = 'v1';

const DEFAULT_REFERENCE_DATA_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * TTL for cached reference-data lists (moderators, session types, projects).
 * Overridable via VITE_REFERENCE_DATA_CACHE_TTL_MS (e.g. in .env or .env.local)
 * without a code change — useful for local/QA testing where the backend gets
 * pointed at a different DB and a stale cached list would otherwise persist
 * for the full default window.
 */
export const REFERENCE_DATA_CACHE_TTL_MS = (() => {
  const raw = import.meta.env.VITE_REFERENCE_DATA_CACHE_TTL_MS;
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_REFERENCE_DATA_CACHE_TTL_MS;
})();

interface CacheEnvelope<T> {
  updatedAt: number;
  value: T;
}

function fullKey(key: string): string {
  return `localStorageCache:${SCHEMA_VERSION}:${key}`;
}

/** Returns the cached value if present and within `ttlMs` of when it was set, else null. */
export function getCached<T>(key: string, ttlMs: number): T | null {
  try {
    const raw = localStorage.getItem(fullKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEnvelope<T>;
    if (typeof parsed?.updatedAt !== 'number') return null;
    if (Date.now() - parsed.updatedAt > ttlMs) return null;
    return parsed.value;
  } catch {
    return null;
  }
}

export function setCached<T>(key: string, value: T): void {
  try {
    const payload: CacheEnvelope<T> = {updatedAt: Date.now(), value};
    localStorage.setItem(fullKey(key), JSON.stringify(payload));
  } catch {
    // Storage full/disabled/unavailable (private browsing, quota) — cache is
    // an optimization, not a requirement, so fail silently.
  }
}

export function clearCached(key: string): void {
  try {
    localStorage.removeItem(fullKey(key));
  } catch {
    // no-op
  }
}

/**
 * Clears every entry this utility has ever written, regardless of key.
 * Reference-data lists (moderators, session types, projects) can be
 * role/project-scoped, so this is called on sign-out — otherwise a
 * different user logging in on the same browser could briefly see the
 * previous user's cached data until the TTL lapses.
 */
export function clearAllCached(): void {
  try {
    const prefix = 'localStorageCache:';
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // no-op
  }
}
